// Notification helpers that can be used from React and non-React modules.

import { NotificationData } from '../services/socketService';
import toast from 'react-hot-toast';

export type LocalNotification = Omit<NotificationData, 'id' | 'timestamp' | 'read'>;

let handler: ((n: LocalNotification) => void) | null = null;

export function registerNotificationHandler(fn: (n: LocalNotification) => void) {
  handler = fn;
}

export function notify(notification: LocalNotification) {
  const durationMs = (notification.duration ?? 6) * 1000;
  const title = notification.title?.trim();
  const message = notification.message?.trim() || 'Ha ocurrido un evento';
  const composed = title ? `${title}: ${message}` : message;

  try {
    if (handler) {
      handler(notification);
      return;
    }

    switch (notification.type) {
      case 'success':
        toast.success(composed, { duration: durationMs });
        break;
      case 'warning':
        toast(composed, {
          icon: '⚠️',
          duration: durationMs,
        });
        break;
      case 'error':
        toast.error(composed, { duration: durationMs });
        break;
      default:
        toast(composed, { duration: durationMs });
        break;
    }
  } catch (err) {
    console.error('[notificationCenter] Failed to deliver notification', err);
  }
}

const defaultDurations = {
  success: 5,
  info: 6,
  warning: 7,
  error: 8,
} as const;

export function notifySuccess(message: string, title = 'Operación exitosa') {
  notify({
    type: 'success',
    priority: 'medium',
    title,
    message,
    autoClose: true,
    duration: defaultDurations.success,
  });
}

export function notifyInfo(message: string, title = 'Información') {
  notify({
    type: 'info',
    priority: 'medium',
    title,
    message,
    autoClose: true,
    duration: defaultDurations.info,
  });
}

export function notifyWarning(message: string, title = 'Atención') {
  notify({
    type: 'warning',
    priority: 'high',
    title,
    message,
    autoClose: true,
    duration: defaultDurations.warning,
  });
}

export function notifyError(message: string, title = 'Error') {
  notify({
    type: 'error',
    priority: 'high',
    title,
    message,
    autoClose: true,
    duration: defaultDurations.error,
  });
}
